import React, { useEffect, useState } from 'react'

import { useHistory, useParams } from 'react-router-dom'
import { Box, Button, Tooltip } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'
import { reverse } from 'named-urls'
import { HelpCircle as HelpCircleIcon } from 'react-feather'

import {
  Container,
  ContentHeader,
  GuestDriveTour,
  LoadingFeedback,
  Page,
} from 'components'
import {
  PrivacyByDesignForm as PrivacyByDesignGeneralInfoForm,
  PrivacyByDesignQuestionReply,
  PrivacyByDesignReviewActions,
  PrivacyByDesignReviewPanel,
  PrivacyByDesignSendToReviewButton,
} from 'views/PrivacyByDesign/components'

import useAuthGuest from 'hooks/useAuthGuest'
import useFetch from 'hooks/useFetch'
import usePrivacyByDesignAnswers from 'hooks/usePrivacyByDesignAnswers'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'
import { routes } from 'GuestRoutes'
import schema from 'views/PrivacyByDesign/components/PrivacyByDesignForm/schema'
import {
  buildCreatePayload,
  buildDefaultValues,
  buildGeneralInfoPayload,
} from 'views/PrivacyByDesign/components/PrivacyByDesignForm/formMappers'
import { buildPrivacyByDesignFormTutorialSteps } from './driverSteps'

const RESPONSIBLE_FIELD = 'name'

// Criação e edição do projeto pelo portal de convidados. A tela é montada com os
// mesmos componentes do app, injetando os services do portal — o que muda aqui é
// o departamento (o do próprio convidado) e o responsável, que é um nome livre.
const PrivacyByDesignForm = () => {
  const [openTour, setOpenTour] = useState(false)

  const history = useHistory()
  const snackbar = useSnackbar()
  const { privacyByDesignId, organizationSlug } = useParams()
  const { user } = useAuthGuest()

  const isEdit = !!privacyByDesignId
  const mode = isEdit ? 'edit' : 'new'

  const { response, isLoading, refresh } = useFetch(
    service.guestPortal.privacyByDesign.show,
    { privacyByDesignId },
    [privacyByDesignId],
    isEdit,
  )

  const privacyByDesign = isEdit ? response?.data?.privacyByDesign : null

  // O perfil do convidado traz apenas o id do departamento, então o nome exibido
  // no campo (somente leitura) vem da lista de departamentos do portal.
  const { response: responseDepartments } = useFetch(
    service.guestPortal.departments.get,
    { perPage: 99999, order: 'name ASC', minimal: true },
    [],
    !isEdit,
  )

  const userDepartment = (responseDepartments?.data?.departments || []).find(
    (department) => department.id === user?.departmentId,
  )

  const {
    evidencesByAnswerId,
    loadPrivacyByDesign,
    questions,
    removeEvidence,
    reopenAnswer,
    saveAnswer,
    saveStateByQuestionId,
  } = usePrivacyByDesignAnswers({
    privacyByDesignId,
    service: service.guestPortal.privacyByDesign,
  })

  const formMethods = useForm({
    validationSchema: schema({ mode, responsibleField: RESPONSIBLE_FIELD }),
    defaultValues: buildDefaultValues(privacyByDesign, {
      mode,
      responsibleField: RESPONSIBLE_FIELD,
    }),
  })

  const { reset, setValue } = formMethods

  const redirectToList = () =>
    history.push(reverse(routes.privacyByDesign.list, { organizationSlug }))

  // Na edição o card salva apenas as informações gerais; na criação o submit
  // cria o projeto e segue para a edição, onde as questões são respondidas.
  const handleSubmit = async (values) => {
    try {
      if (isEdit) {
        await service.guestPortal.privacyByDesign.update({
          privacyByDesignId,
          ...buildGeneralInfoPayload(values, {
            responsibleField: RESPONSIBLE_FIELD,
          }),
        })

        snackbar.open({
          message: 'Informações gerais salvas com sucesso!',
          variant: 'success',
        })

        return
      }

      const createResponse = await service.guestPortal.privacyByDesign.create(
        buildCreatePayload(values, { responsibleField: RESPONSIBLE_FIELD }),
      )

      history.push(
        reverse(routes.privacyByDesign.edit, {
          organizationSlug,
          privacyByDesignId: createResponse?.data?.privacyByDesign?.id,
        }),
      )
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar o projeto.',
        variant: 'error',
      })
    }
  }

  useEffect(() => {
    if (privacyByDesign) {
      reset(
        buildDefaultValues(privacyByDesign, {
          mode,
          responsibleField: RESPONSIBLE_FIELD,
        }),
      )
      loadPrivacyByDesign(privacyByDesign)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacyByDesign, reset])

  // O perfil do convidado chega depois do primeiro render: na criação o projeto
  // é do departamento dele e o responsável já vem preenchido com o próprio nome.
  useEffect(() => {
    if (isEdit || !user) return

    if (user.departmentId) setValue('departmentId', user.departmentId)
    if (user.responsibleName) setValue('responsibleName', user.responsibleName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isEdit])

  return (
    <Page title={isEdit ? 'Editar Projeto' : 'Novo Projeto'}>
      <Container maxWidth={false}>
        <LoadingFeedback open={isEdit && isLoading} />
        {/* Sem breadcrumbs: eles são resolvidos pelas rotas do app e não fazem
            sentido no portal, que tem só a listagem e o projeto. */}
        <ContentHeader
          title={isEdit ? 'Editar Projeto' : 'Novo Projeto'}
          historyBreadcumbs={false}
        >
          <Tooltip title="Iniciar o tour guiado">
            <Button
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={() => setOpenTour(true)}
              fullWidth
            >
              Tutorial
            </Button>
          </Tooltip>
        </ContentHeader>
        <FormContext {...formMethods}>
          <PrivacyByDesignGeneralInfoForm
            mode={mode}
            privacyByDesign={privacyByDesign || {}}
            responsibleField={RESPONSIBLE_FIELD}
            quizTemplatesService={
              service.guestPortal.privacyByDesign.listQuizTemplates
            }
            currentDepartmentName={userDepartment?.name || ''}
            currentDepartmentId={user?.departmentId || null}
            onSubmit={handleSubmit}
            cardId={
              constants.privacyByDesign.PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_1
            }
          />
          {isEdit && (
            <>
              <PrivacyByDesignReviewPanel
                privacyByDesign={privacyByDesign}
                actions={
                  <PrivacyByDesignReviewActions
                    privacyByDesign={privacyByDesign}
                    service={service.guestPortal.privacyByDesign}
                    refresh={refresh}
                    onReopen={refresh}
                  />
                }
              />
              <PrivacyByDesignQuestionReply
                questions={questions}
                isLoading={isLoading}
                evidencesByAnswerId={evidencesByAnswerId}
                onRemoveEvidence={removeEvidence}
                onSaveAnswer={saveAnswer}
                onReopenAnswer={reopenAnswer}
                saveStateByQuestionId={saveStateByQuestionId}
                hasReviewerOpinion={!!privacyByDesign?.latestReviewerOpinion}
              />
            </>
          )}
        </FormContext>
        <Box mt={2} gridGap={5} display="flex" justifyContent="flex-end">
          <Box>
            <Button type="button" variant="outlined" onClick={redirectToList}>
              {isEdit ? 'Voltar' : 'Cancelar'}
            </Button>
          </Box>
          {isEdit ? (
            <PrivacyByDesignSendToReviewButton
              privacyByDesignId={privacyByDesignId}
              service={service.guestPortal.privacyByDesign}
              questions={questions}
              onSuccess={redirectToList}
            />
          ) : (
            <Box>
              <Button
                form="privacy-by-design-form"
                type="submit"
                color="primary"
                variant="contained"
              >
                Criar
              </Button>
            </Box>
          )}
        </Box>
      </Container>
      <GuestDriveTour
        stepsMatrix={buildPrivacyByDesignFormTutorialSteps({ isEdit })}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default PrivacyByDesignForm
